import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { SigninForm } from "@lyder-talk/ui";
import { useTranslation } from "react-i18next";
import { signInWithGoogle, getAppUrl } from "@lyder-talk/core";
import { auth } from "@lyder-talk/core/lib/auth-client";

const Signin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [googleLoading, setGoogleLoading] = useState(false);

  const redirectTo = searchParams.get("redirectTo");
  const redirectQuery = redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : "";

  const handleSubmit = async (email: string, password: string) => {
    try {
      const { error } = await auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      toast({ title: t("auth.forms.toasts.welcomeBack", "Welcome back!") });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t("common.errors.generic");

      // The API only says this after the password has been verified, so the
      // address is genuine and sending another link cannot be used to probe
      // accounts. Silent sign-in failure with no way to get the mail again was
      // the dead end this replaces.
      if (/confirm your email/i.test(message)) {
        void auth.resendConfirmation(email, { redirectTo: getAppUrl("/auth/confirm") });
      }

      toast({
        title: t("auth.forms.toasts.error", "Error"),
        description: message,
        variant: "destructive",
      });
      throw new Error(message);
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch {
      toast({
        title: t("auth.forms.toasts.error", "Error"),
        description: t("auth.forms.toasts.googleFailed", "Failed to sign in with Google"),
        variant: "destructive",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <SigninForm
      onSubmit={handleSubmit}
      onNavigateToSignup={() => navigate(`/auth/signup${redirectQuery}`)}
      onNavigateToForgotPassword={() => navigate(`/auth/forgot-password${redirectQuery}`)}
      onGoogleSignIn={handleGoogle}
      googleLoading={googleLoading}
      // The form lives in @lyder-talk/ui, shared with the admin panel, so it
      // cannot read i18next itself — the app supplies the copy.
      copy={{
        showPasswordLabel: t("auth.forms.signin.showPassword"),
        hidePasswordLabel: t("auth.forms.signin.hidePassword"),
        title: t("auth.forms.signin.title"),
        description: t("auth.forms.signin.description"),
        emailLabel: t("auth.forms.signin.emailLabel"),
        emailPlaceholder: t("auth.forms.signin.emailPlaceholder"),
        passwordLabel: t("auth.forms.signin.passwordLabel"),
        forgotPassword: t("auth.forms.signin.forgotPassword"),
        submitting: t("auth.forms.signin.submitting"),
        submit: t("auth.forms.signin.submit"),
        continueWithGoogle: t("auth.forms.signin.continueWithGoogle"),
        newHere: t("auth.forms.signin.newHere"),
        createAccount: t("auth.forms.signin.createAccount"),
        genericError: t("auth.forms.signin.genericError"),
        legal: {
          termsPrefix: t("auth.forms.legal.termsPrefix"),
          termsLink: t("auth.forms.legal.termsLink"),
          andPrefix: t("auth.forms.legal.andPrefix"),
          privacyLink: t("auth.forms.legal.privacyLink"),
          orContinueWith: t("auth.forms.legal.orContinueWith"),
        },
      }}
    />
  );
};

export default Signin;
